/**
 * MappingRHTool — Outil de Mapping RH intégré dans recruteurs.io.
 * --------------------------------------------------------------------
 * Remplace l'ancien iframe externe (`soft-platypus-aaf096.netlify.app`)
 * par un composant React natif aligné sur la charte graphique du site.
 *
 * Flow utilisateur (3 états dans `step`) :
 *   1. "form"     — 5 sliders + textarea, click "Analyser avec l'IA"
 *   2. "lead"     — modale email + prénom (insertion dans table `leads`)
 *   3. "results"  — score visuel + zones de friction + recommandations
 *
 * Backend :
 *   - Edge function Supabase `test-friction` (projet `fxutlubjfhdfhpiqmisk`)
 *   - Table `leads` (même projet)
 *   - Tout est routé via le client `mappingSupabase` dédié.
 */

import { useState, useMemo, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw,
  MessageSquare,
  CloudSun,
  LayoutGrid,
  Rocket,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Info,
  Mail,
  Loader2,
  X,
  ArrowRight,
} from 'lucide-react';
import {
  mappingSupabase,
  edgeFunctionUrl,
  mappingAnonKey,
  isMappingClientReady,
} from '../../lib/mappingClient';

// ── Types ────────────────────────────────────────────────────────────

type Step = 'form' | 'lead' | 'results';

interface FormState {
  autonomy: number;
  feedback: number;
  ambiance: number;
  structure: number;
  initiative: number;
  description: string;
}

interface AnalysisResult {
  score: number;
  frictionAreas: string[];
  recommendations: string[];
}

// ── Configuration des sliders ────────────────────────────────────────

const SLIDERS: {
  key: keyof Omit<FormState, 'description'>;
  label: string;
  icon: ReactNode;
  leftLabel: string;
  rightLabel: string;
}[] = [
  {
    key: 'autonomy',
    label: 'Niveau d’autonomie',
    icon: <RefreshCw className="w-5 h-5" />,
    leftLabel: 'Très encadré',
    rightLabel: 'Très autonome',
  },
  {
    key: 'feedback',
    label: 'Style de feedback',
    icon: <MessageSquare className="w-5 h-5" />,
    leftLabel: 'Très direct',
    rightLabel: 'Très diplomate',
  },
  {
    key: 'ambiance',
    label: 'Ambiance',
    icon: <CloudSun className="w-5 h-5" />,
    leftLabel: 'Zen',
    rightLabel: 'Sous pression',
  },
  {
    key: 'structure',
    label: 'Structuration',
    icon: <LayoutGrid className="w-5 h-5" />,
    leftLabel: 'Très cadrée',
    rightLabel: 'Freestyle',
  },
  {
    key: 'initiative',
    label: 'Prise d’initiative',
    icon: <Rocket className="w-5 h-5" />,
    leftLabel: 'Exécution',
    rightLabel: 'Moteur',
  },
];

// ── Composant principal ──────────────────────────────────────────────

export default function MappingRHTool() {
  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState<FormState>({
    autonomy: 3,
    feedback: 3,
    ambiance: 3,
    structure: 3,
    initiative: 3,
    description: '',
  });
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);

  // Soumet le formulaire à l'edge function test-friction
  async function handleAnalyze(e: React.FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) {
      setError('Décrivez le profil recherché avant de lancer l’analyse.');
      return;
    }
    setError(null);
    setIsAnalyzing(true);

    const apiUrl = edgeFunctionUrl('test-friction');
    if (!apiUrl || !mappingAnonKey) {
      setError(
        "L'outil de matching n'est pas configuré. Variables d'environnement Supabase manquantes.",
      );
      setIsAnalyzing(false);
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${mappingAnonKey}`,
          'Content-Type': 'application/json',
          apikey: mappingAnonKey,
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API ${response.status} : ${text.slice(0, 120)}`);
      }

      const result = (await response.json()) as AnalysisResult;
      setAnalysis(result);
      setShowLeadModal(true);
      setStep('lead');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Impossible de contacter l'analyse. Réessayez dans un instant.";
      setError(msg);
    } finally {
      setIsAnalyzing(false);
    }
  }

  // Une fois le lead capturé → on passe à l'écran results
  function handleLeadSubmitted() {
    setShowLeadModal(false);
    setStep('results');
  }

  // Reset complet
  function handleReset() {
    setForm({
      autonomy: 3,
      feedback: 3,
      ambiance: 3,
      structure: 3,
      initiative: 3,
      description: '',
    });
    setAnalysis(null);
    setStep('form');
    setError(null);
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      {!isMappingClientReady && (
        <ConfigWarning />
      )}

      <AnimatePresence mode="wait">
        {step === 'form' && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
          >
            <FormCard
              form={form}
              setForm={setForm}
              onAnalyze={handleAnalyze}
              isAnalyzing={isAnalyzing}
              error={error}
            />
          </motion.div>
        )}

        {step === 'results' && analysis && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
          >
            <ResultsCard analysis={analysis} onReset={handleReset} />
          </motion.div>
        )}
      </AnimatePresence>

      {showLeadModal && analysis && (
        <LeadCaptureModal
          score={analysis.score}
          onClose={() => setShowLeadModal(false)}
          onSubmitted={handleLeadSubmitted}
        />
      )}
    </div>
  );
}

// ── Carte formulaire ─────────────────────────────────────────────────

interface FormCardProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onAnalyze: (e: React.FormEvent) => void;
  isAnalyzing: boolean;
  error: string | null;
}

function FormCard({ form, setForm, onAnalyze, isAnalyzing, error }: FormCardProps) {
  return (
    <form
      onSubmit={onAnalyze}
      className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm space-y-8"
    >
      <div>
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
          Caractéristiques de votre équipe actuelle
        </p>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight">
          Décrivez votre équipe.{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
            En 5 curseurs.
          </span>
        </h3>
      </div>

      <div className="space-y-7">
        {SLIDERS.map(({ key, label, icon, leftLabel, rightLabel }) => (
          <SliderRow
            key={key}
            label={label}
            icon={icon}
            value={form[key]}
            onChange={(v) => setForm((prev) => ({ ...prev, [key]: v }))}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
          />
        ))}
      </div>

      <div className="space-y-3 pt-2">
        <label
          htmlFor="profile-description"
          className="block text-sm font-semibold text-[#30475e]"
        >
          Décrivez le profil recherché ou collez votre offre d’emploi
        </label>
        <textarea
          id="profile-description"
          rows={5}
          value={form.description}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, description: e.target.value }))
          }
          placeholder="Exemple : Nous recherchons un commercial terrain expérimenté, autonome, à l'aise avec un management décentralisé, capable de prendre des initiatives sur le développement du portefeuille…"
          className="w-full p-4 rounded-2xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484] focus:outline-none transition-all text-base text-neutral-800 placeholder:text-neutral-400 leading-relaxed"
          required
        />
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl bg-red-50 px-4 py-3 ring-1 ring-red-200">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 leading-snug">{error}</p>
        </div>
      )}

      <div className="flex justify-center pt-2">
        <button
          type="submit"
          disabled={isAnalyzing}
          className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Analyse en cours…
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Analyser avec l’IA
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ── Slider ──────────────────────────────────────────────────────────

interface SliderRowProps {
  label: string;
  icon: ReactNode;
  value: number;
  onChange: (v: number) => void;
  leftLabel: string;
  rightLabel: string;
}

function SliderRow({ label, icon, value, onChange, leftLabel, rightLabel }: SliderRowProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-sm">
          {icon}
        </div>
        <p className="text-base font-semibold text-[#30475e]">{label}</p>
      </div>

      <input
        type="range"
        min={1}
        max={5}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="mapping-slider w-full h-2 bg-neutral-200 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #d03484 0%, #d47254 ${
            ((value - 1) / 4) * 100
          }%, #e5e5e5 ${((value - 1) / 4) * 100}%, #e5e5e5 100%)`,
        }}
      />

      <div className="flex items-center justify-between gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            onClick={() => onChange(n)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all ${
              n === value
                ? 'bg-gradient-to-r from-[#d03484] to-[#d47254] text-white shadow-md scale-110'
                : 'bg-white text-neutral-500 ring-1 ring-neutral-200 hover:ring-neutral-300'
            }`}
            aria-label={`Niveau ${n}`}
          >
            {n}
          </button>
        ))}
      </div>

      <div className="flex justify-between text-xs text-neutral-500 px-1">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

// ── Carte résultats ──────────────────────────────────────────────────

interface ResultsCardProps {
  analysis: AnalysisResult;
  onReset: () => void;
}

function ResultsCard({ analysis, onReset }: ResultsCardProps) {
  const { score, frictionAreas, recommendations } = analysis;

  const verdict = useMemo(() => {
    if (score > 80)
      return {
        Icon: CheckCircle2,
        title: 'Excellente compatibilité',
        text: "L'intégration devrait être fluide. Quelques points d'attention seulement.",
      };
    if (score > 60)
      return {
        Icon: Info,
        title: 'Bonne compatibilité',
        text: "Recrutement viable, à condition de traiter les points de friction listés.",
      };
    return {
      Icon: AlertTriangle,
      title: 'Compatibilité moyenne',
      text: "Des ajustements managériaux sérieux seront nécessaires avant de signer.",
    };
  }, [score]);

  const VerdictIcon = verdict.Icon;
  const dashOffset = 100 - score;

  function shareByEmail() {
    const subject = encodeURIComponent('Mon rapport Mapping RH — recruteurs.io');
    const body = encodeURIComponent(
      [
        `Score de compatibilité : ${score}%`,
        '',
        'Zones de friction potentielles :',
        ...frictionAreas.map((a) => `- ${a}`),
        '',
        'Recommandations pour le manager :',
        ...recommendations.map((r, i) => `${i + 1}. ${r}`),
        '',
        'Généré sur https://recruteurs.io/ressources/mapping-rh',
      ].join('\n'),
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  return (
    <div className="space-y-6">
      {/* Bloc score */}
      <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm">
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3 text-center">
          Résultat de l’analyse
        </p>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight text-center mb-8">
          Score de compatibilité{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
            candidat ↔ équipe.
          </span>
        </h3>

        <div className="flex flex-col items-center gap-6">
          <div className="relative w-44 h-44">
            <svg viewBox="0 0 36 36" className="w-44 h-44 -rotate-90">
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#d03484" />
                  <stop offset="100%" stopColor="#d47254" />
                </linearGradient>
              </defs>
              <circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="none"
                stroke="#f3f4f6"
                strokeWidth="2.5"
              />
              <motion.circle
                cx="18"
                cy="18"
                r="15.9155"
                fill="none"
                stroke="url(#scoreGradient)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="100"
                initial={{ strokeDashoffset: 100 }}
                animate={{ strokeDashoffset: dashOffset }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-5xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                {score}
                <span className="text-2xl">%</span>
              </span>
            </div>
          </div>

          <div className="flex items-start gap-3 max-w-md">
            <VerdictIcon className="w-6 h-6 text-[#d03484] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-lg font-semibold text-[#30475e] mb-1">
                {verdict.title}
              </p>
              <p className="text-sm text-neutral-600 leading-relaxed">
                {verdict.text}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Zones de friction */}
      <div className="rounded-[2rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h4 className="text-xl font-semibold text-[#30475e]">
            Zones de friction potentielles
          </h4>
        </div>

        <ul className="space-y-3">
          {frictionAreas.map((area, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3 rounded-2xl bg-[#fbfaf8] px-5 py-4 ring-1 ring-neutral-200"
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white ring-2 ring-[#d03484]/30 flex items-center justify-center text-xs font-bold text-[#d03484]">
                {idx + 1}
              </span>
              <span className="text-base text-neutral-800 leading-snug">{area}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Recommandations */}
      <div className="rounded-[2rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h4 className="text-xl font-semibold text-[#30475e]">
            Recommandations pour le manager
          </h4>
        </div>

        <ol className="space-y-3">
          {recommendations.map((rec, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3 rounded-2xl bg-[#fbfaf8] px-5 py-4 ring-1 ring-neutral-200"
            >
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] text-white flex items-center justify-center text-xs font-bold">
                {idx + 1}
              </span>
              <span className="text-base text-neutral-800 leading-snug">{rec}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
        <button
          onClick={shareByEmail}
          className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-5 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
        >
          <Mail className="w-5 h-5" />
          Recevoir le rapport par email
        </button>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <RefreshCw className="w-5 h-5" />
          Refaire un test
        </button>
      </div>

      <p className="text-xs text-neutral-400 italic text-center">
        Ce rapport est généré à titre indicatif. Pour une analyse approfondie,
        contactez l’équipe recruteurs.io.
      </p>
    </div>
  );
}

// ── Modale de capture de lead ───────────────────────────────────────

interface LeadCaptureModalProps {
  score: number;
  onClose: () => void;
  onSubmitted: () => void;
}

function LeadCaptureModal({ score, onClose, onSubmitted }: LeadCaptureModalProps) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [wantsResources, setWantsResources] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !firstName.trim()) {
      setError('Renseignez votre prénom et votre email professionnel.');
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      if (mappingSupabase) {
        const { error: insertError } = await mappingSupabase
          .from('leads')
          .upsert(
            {
              email: email.trim().toLowerCase(),
              first_name: firstName.trim(),
              source: 'test_mapping_rh',
              score: String(score),
              wants_resources: wantsResources,
            },
            { onConflict: 'email' },
          );
        if (insertError) {
          // On ne bloque pas l'utilisateur si l'enregistrement plante :
          // l'objectif principal est qu'il voie ses résultats. On log
          // l'erreur pour debug et on continue.
          console.error('[mapping-rh] Erreur insert lead:', insertError.message);
        }
      }
      onSubmitted();
    } catch (err) {
      console.error('[mapping-rh] Erreur capture lead:', err);
      // Idem : on laisse l'utilisateur voir ses résultats même si Supabase
      // est indisponible.
      onSubmitted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-950/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="relative bg-white rounded-[2rem] shadow-2xl max-w-md w-full p-8 ring-1 ring-neutral-200"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center transition-colors"
          aria-label="Fermer"
        >
          <X className="w-5 h-5 text-neutral-600" />
        </button>

        <div className="text-center mb-6">
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-2">
            Vos résultats sont prêts
          </p>
          <h3 className="text-2xl font-bold tracking-tight text-[#30475e] leading-tight mb-3">
            Score de compatibilité :{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              {score}%
            </span>
          </h3>
          <p className="text-sm text-neutral-600 leading-relaxed">
            Indiquez votre prénom et votre email pro pour accéder au détail —
            zones de friction, recommandations, plan d’action manager.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="first-name" className="block text-xs font-semibold text-neutral-700 mb-1.5">
              Prénom
            </label>
            <input
              id="first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484] focus:outline-none transition-all text-base"
              placeholder="Camille"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-neutral-700 mb-1.5">
              Email professionnel
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484] focus:outline-none transition-all text-base"
              placeholder="camille@votre-entreprise.fr"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer text-sm text-neutral-700 leading-snug">
            <input
              type="checkbox"
              checked={wantsResources}
              onChange={(e) => setWantsResources(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-[#d03484]"
            />
            <span>
              J’accepte de recevoir occasionnellement des ressources RH
              de la part de recruteurs.io. Pas de spam, désabonnement en 1 clic.
            </span>
          </label>

          {error && (
            <p className="text-sm text-red-600 leading-snug">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Voir mes résultats <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-neutral-400 mt-4 text-center">
          Vos données restent confidentielles et ne sont jamais revendues.
        </p>
      </motion.div>
    </motion.div>
  );
}

// ── Avertissement de configuration (dev only) ───────────────────────

function ConfigWarning() {
  return (
    <div className="mb-6 flex items-start gap-3 rounded-2xl bg-amber-50 px-5 py-4 ring-1 ring-amber-200">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-900 leading-snug">
        <p className="font-semibold mb-1">
          Configuration manquante
        </p>
        <p>
          L’outil de Mapping RH nécessite les variables d’environnement{' '}
          <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">
            VITE_MAPPING_SUPABASE_URL
          </code>{' '}
          et{' '}
          <code className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">
            VITE_MAPPING_SUPABASE_ANON_KEY
          </code>
          . Contactez l’admin du site.
        </p>
      </div>
    </div>
  );
}
